"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  UserSpecialReaction,
  getTotalReactionCountForSpecial,
} from "@/data/userSpecialsReactions";

import { Database } from "@punchup/data/supabase.types";
import { SpecialRow } from "@/data/specials";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface UseSpecialReactionsOptions {
  specialId: SpecialRow["id"];
  type?: UserSpecialReaction["type"];
}

export function useSpecialReactions({
  specialId,
  type = "clap",
}: UseSpecialReactionsOptions) {
  const queryFn = async (): Promise<number> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const count = await getTotalReactionCountForSpecial(
        supabase,
        specialId,
        type
      );
      return count;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `Error fetching ${type} reactions inside useSpecialReactions hook`,
        },
        tags: {
          supabase: true,
          feature: SentryFeatureTags.Specials,
          specialId,
          reactionType: type,
        },
      });
      throw error;
    }
  };

  const {
    data: count,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["special-reactions", specialId, type],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { count: count ?? 0, isLoading, error, refetch };
}
